import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useScrollVisibility } from '../hooks/useScrollVisibility';
import { openGoogleReview, resolveGoogleReviewUrl } from '../lib/google-review-url';

function GoogleGIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export default function LeaveReviewButton() {
  const { settings, loading: settingsLoading } = useSettings();
  const scrollVisible = useScrollVisibility();
  const [mounted, setMounted] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const googleReviewUrl = resolveGoogleReviewUrl(settings);

  useEffect(() => {
    if (settingsLoading) return;
    const t = setTimeout(() => setMounted(true), 2000);
    return () => clearTimeout(t);
  }, [settingsLoading]);

  if (!mounted || !googleReviewUrl) return null;

  return (
    <AnimatePresence>
      {scrollVisible && (
        <motion.div
          className="fixed z-[9999] left-4 sm:left-6 bottom-24 sm:bottom-6"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        >
          <AnimatePresence>
            {tooltipVisible && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                className="absolute bottom-full left-0 mb-3 px-4 py-2 rounded-2xl bg-white text-gray-900 text-xs font-semibold shadow-xl border border-gray-100 whitespace-nowrap"
              >
                Laisser un avis Google ⭐
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative">
            <span className="absolute w-full h-full rounded-full bg-[#ff033c] animate-ping opacity-30" />
            <motion.button
              type="button"
              onClick={() => openGoogleReview(settings)}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
              onFocus={() => setTooltipVisible(true)}
              onBlur={() => setTooltipVisible(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Laisser un avis sur Google"
              className="relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center bg-gradient-to-br from-[#ff033c] to-[#d14545] hover:from-[#e9023a] hover:to-[#ff033c] transition-colors"
            >
              <GoogleGIcon className="w-8 h-8" />
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Bouton compact pour la barre rouge en haut du site */
export function HeaderReviewButton() {
  const { settings, loading: settingsLoading } = useSettings();
  const googleReviewUrl = resolveGoogleReviewUrl(settings);

  if (settingsLoading || !googleReviewUrl) return null;

  return (
    <button
      type="button"
      onClick={() => openGoogleReview(settings)}
      className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/25 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wide transition-all hover:scale-105 active:scale-95 shrink-0"
      aria-label="Laisser un avis Google"
    >
      <Star className="w-3.5 h-3.5 fill-white shrink-0" />
      <span className="hidden sm:inline">Laisser un avis</span>
      <span className="sm:hidden">Avis</span>
    </button>
  );
}
